import {
  useQpmCalculationDeleteMutation,
  useQpmCalculationShowQuery,
} from "@/services/backend/qpm/calculations/api/calculation-api";
import { useNavigate, useParams } from "react-router";
import t from "@/lang/lang";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Card } from "@/shared/components/ui/card";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { useToast } from "@/shared/hooks/use-toast";
import { useEffect } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Trash2 } from "lucide-react";
import { SelectRequirementButtonComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/select-requirement-component";

import { CalculationRouteNav } from "@/routes/processes/[processId]/qpm/_components/nav-links/calculation-nav";
import { CalculationDataProvider } from "@/services/backend/qpm/calculations/hooks/calculation-context";

export function CalculationRoute() {
  const { calculationId } = useParams();
  const {
    data: calculation,
    isLoading,
    error,
  } = useQpmCalculationShowQuery(
    { id: calculationId! },
    { skip: !calculationId },
  );

  const links = [
    { to: CalculationRouteNav.MaterialCost, label: t("Materialkosten") },
    { to: CalculationRouteNav.Installation, label: t("Einrichtung") },
    { to: CalculationRouteNav.Service, label: t("Service") },
    { to: CalculationRouteNav.Rental, label: t("Miete") },
    { to: CalculationRouteNav.TotalOverview, label: t("Gesamtübersicht") },
  ];

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return (
    calculationId && (
      <PersistenceContextProvider>
        <CalculationDataProvider calculationId={calculationId}>
          <DocumentPage>
            <DocumentPageHeader>
              <DocumentPageNavigation>
                {links.map((link) => (
                  <DocumentPageNavigationLink key={link.to} to={link.to}>
                    {link.label}
                  </DocumentPageNavigationLink>
                ))}
              </DocumentPageNavigation>
              {calculation && (
                <DocumentPageActions>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm">
                      {t("Basis-Bestandsaufnahme")}:
                    </span>
                    <SelectRequirementButtonComponent
                      calculationId={calculationId}
                      processId={calculation.processArtefact?.processId}
                      selectedRequirementId={calculation.boundRequirementId}
                    />
                  </div>
                  <DeleteCalculationDialog calculationId={calculation.id} />
                  <PersistenceStateUsingContext className="mr-2" />
                </DocumentPageActions>
              )}
            </DocumentPageHeader>
            <DocumentPageContent>
              <DocumentPageNavigationOutlet />
            </DocumentPageContent>
          </DocumentPage>
        </CalculationDataProvider>
      </PersistenceContextProvider>
    )
  );
}

function DeleteCalculationDialog(props: { calculationId?: string }) {
  const { calculationId } = props;
  const [deleteCalculation, { isLoading, error, isSuccess, reset }] =
    useQpmCalculationDeleteMutation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const onClick = () => {
    if (isLoading || !calculationId) {
      return;
    }
    deleteCalculation({ id: calculationId });
  };

  useEffect(() => {
    if (isSuccess) {
      toast({
        title: t("Kalkulation gelöscht"),
        variant: "success",
      });
      navigate(`..`);
      reset();
    }
  }, [error, isSuccess, navigate, reset, toast]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2 className="h-5 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Kalkulation wirklich löschen?")}</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onClick}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
